import { db } from '../lib/firebase';
import { doc, setDoc, updateDoc, onSnapshot, collection, addDoc, serverTimestamp, getDoc, query, where } from 'firebase/firestore';

export type CallType = 'audio' | 'video';

export interface CallRecord {
  id: string;
  callerId: string;
  calleeId: string;
  type: CallType;
  offer?: any;
  answer?: any;
  createdAt?: any;
  endedAt?: any;
}

export async function createCall(callerId: string, calleeId: string, type: CallType): Promise<string> {
  const ref = doc(collection(db, 'calls'));
  await setDoc(ref, {
    callerId,
    calleeId,
    type,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function setOffer(callId: string, offer: any) {
  const ref = doc(db, 'calls', callId);
  await updateDoc(ref, { offer });
}

export async function setAnswer(callId: string, answer: any) {
  const ref = doc(db, 'calls', callId);
  await updateDoc(ref, { answer });
}

export function subscribeCall(callId: string, cb: (rec: CallRecord) => void) {
  const ref = doc(db, 'calls', callId);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) return;
    const d = snap.data() as any;
    cb({ id: snap.id, ...d });
  });
}

export async function addIceCandidate(callId: string, who: 'caller' | 'callee', candidate: RTCIceCandidate) {
  const ref = collection(db, 'calls', callId, `candidates_${who}`);
  const payload = {
    candidate: candidate?.candidate,
    sdpMid: candidate?.sdpMid ?? null,
    sdpMLineIndex: candidate?.sdpMLineIndex ?? null,
    usernameFragment: (candidate as any)?.usernameFragment ?? null,
    createdAt: serverTimestamp(),
  };
  await addDoc(ref, payload);
}

export function subscribeIceCandidates(callId: string, who: 'caller' | 'callee', cb: (candidate: RTCIceCandidate) => void) {
  const ref = collection(db, 'calls', callId, `candidates_${who}`);
  return onSnapshot(ref, (snap) => {
    snap.docChanges().forEach((ch) => {
      const data = ch.doc.data() as any;
      const init: RTCIceCandidateInit = {
        candidate: data.candidate,
        sdpMid: data.sdpMid ?? undefined,
        sdpMLineIndex: data.sdpMLineIndex ?? undefined,
      };
      if (data.usernameFragment) (init as any).usernameFragment = data.usernameFragment;
      cb(new RTCIceCandidate(init));
    });
  });
}

export async function endCall(callId: string) {
  const ref = doc(db, 'calls', callId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  await updateDoc(ref, { endedAt: serverTimestamp() });
}

// Subscribe to incoming calls for a user (callee). Opens when offer exists and not ended.
export function subscribeIncomingCalls(userId: string, cb: (rec: CallRecord) => void) {
  const callsCol = collection(db, 'calls');
  const q = query(callsCol, where('calleeId', '==', userId));
  return onSnapshot(q, (snap) => {
    snap.docChanges().forEach((ch) => {
      const d = ch.doc.data() as any;
      const createdAtMs = d?.createdAt?.toMillis ? d.createdAt.toMillis() : 0;
      const isRecent = createdAtMs > 0 ? (Date.now() - createdAtMs) < 5 * 60 * 1000 : true;
      if (d.offer && !d.answer && !d.endedAt && isRecent) {
        cb({ id: ch.doc.id, ...d });
      }
    });
  });
}